// ---------------------------------------------------------------------------
// WorkspaceService — HTTP-backed implementation against WorkflowSvc.
//
// Talks to /Workspaces on the configured API base URL. Response shapes mirror
// the C# DTOs in src/services/WorkflowSvc/Api/Contracts/Workspaces (camelCase
// JSON via ASP.NET Core defaults).
// ---------------------------------------------------------------------------

import type { IHttpClient } from '../api/client.js';
import { AppError } from '../api/errors.js';
import type { CollectionResponse } from '../types/common.js';
import type {
  Workspace,
  WorkspacePatchBody,
  WorkspaceSummary,
  WorkspaceWriteBody,
} from '../types/workspace.js';

import type { IWorkspaceService } from './workspaceService.interface.js';
import {
  WorkspaceAlreadyExistsError,
  WorkspaceConcurrencyError,
  WorkspaceEtagMissingError,
  WorkspaceNotFoundError,
} from './workspaceService.interface.js';

const ROUTES = {
  workspaces: 'Workspaces',
  workspace: (name: string) => `Workspaces/${encodeURIComponent(name)}`,
} as const;

function isHttpStatus(err: unknown, status: number): boolean {
  return err instanceof AppError && err.statusCode === status;
}

export class WorkspaceService implements IWorkspaceService {
  constructor(private readonly client: IHttpClient) {}

  async list(): Promise<WorkspaceSummary[]> {
    const { data } = await this.client.get<
      CollectionResponse<WorkspaceSummary>
    >(ROUTES.workspaces);
    return data.value;
  }

  async get(name: string): Promise<Workspace> {
    try {
      const { data, headers } = await this.client.get<Workspace>(
        ROUTES.workspace(name),
      );
      const etag = headers?.get('ETag');
      if (etag == null || etag.length === 0) {
        throw new WorkspaceEtagMissingError(name);
      }
      return { ...data, etag };
    } catch (err) {
      if (isHttpStatus(err, 404)) throw new WorkspaceNotFoundError(name);
      throw err;
    }
  }

  async create(name: string, body: WorkspaceWriteBody): Promise<Workspace> {
    try {
      const { data } = await this.client.put<Workspace>(
        ROUTES.workspace(name),
        body,
        { headers: { 'If-None-Match': '*' } },
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 412)) throw new WorkspaceAlreadyExistsError(name);
      throw err;
    }
  }

  async patch(
    name: string,
    body: WorkspacePatchBody,
    etag: string,
  ): Promise<Workspace> {
    try {
      const { data, headers } = await this.client.patch<Workspace>(
        ROUTES.workspace(name),
        body,
        { headers: { 'If-Match': etag } },
      );
      const updatedEtag = headers?.get('ETag');
      if (updatedEtag == null || updatedEtag.length === 0) {
        throw new WorkspaceEtagMissingError(name);
      }
      return { ...data, etag: updatedEtag };
    } catch (err) {
      if (isHttpStatus(err, 404)) throw new WorkspaceNotFoundError(name);
      if (isHttpStatus(err, 412)) throw new WorkspaceConcurrencyError(name);
      throw err;
    }
  }

  async delete(name: string): Promise<void> {
    try {
      await this.client.delete<void>(ROUTES.workspace(name));
    } catch (err) {
      if (isHttpStatus(err, 404)) throw new WorkspaceNotFoundError(name);
      throw err;
    }
  }
}
